import { Enums } from "@shared";

interface OptionalFlowProps {
    hasPaymentMethods?: boolean;
    hasDefaultPaymentMethod?: boolean;
    hasSecondaryPaymentMethod?: boolean;
    resolveDefaults?: boolean;
    isAutoPay?: boolean;
    creditChecked?: boolean;
    idProductType?: number
    skipPayors?: boolean;
    enroll?: boolean;
    update?: boolean;
    amount?: any;
    cancel?: boolean;
    isAutopayLate?: boolean;
    addDefaultPM?: boolean;
    makePayment?: boolean;
    missingAccounts?: any[]
}

export const Flows = {
    updateEnrolledInvoice: {
        id: `updateEnrolledInvoice`,
        steps: [
            `/guarantor`,
            `/guarantor/invoice/payors`,
            `/update-enrollment/summary`,
            `/guarantor`
        ]
    },
    justPay: {
        id: `justPay`,
        steps: [
            `/signup-completed`,
            `/make-payment`,
            `/confirm-payment`,
            `/`
        ]
    },

    setupSingle: {
        id: `setupSingle`,
        steps: [
            `/signup`,
            `/welcome`,
            `/information-safety`,
            {
                path: `/existing-payment-methods`,
                optional: ({ hasPaymentMethods, hasDefaultPaymentMethod }: OptionalFlowProps) => !hasPaymentMethods || hasDefaultPaymentMethod
            },
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/confirm-autopay`,
                optional: ({ isAutoPay }: OptionalFlowProps) => isAutoPay
            },
            `/`
        ]
    },
    setupMultiple: {
        id: `setupMultiple`,
        steps: [
            `/signup`,
            `/welcome`,
            `/information-safety`,
            `/guarantor/multiple-accounts-info`,
            {
                path: `/existing-payment-methods`,
                optional: ({ hasPaymentMethods }: OptionalFlowProps) => !hasPaymentMethods
            },
            {
                path: `/payment-method-selector`,
                // TODO: type
                optional: ({ missingAccounts }: OptionalFlowProps) => !missingAccounts
            },
            {
                path: `/default-payment-method`,
                // TODO: type
                optional: ({ missingAccounts }: OptionalFlowProps) => !missingAccounts
            },
            `/multi-autopay`,
            `/`
        ]
    },
    selection: {
        id: `selection`,
        steps: [
            `/guarantor`,
            `/steps`,
            `/product`
        ]
    },
    // See if these can be condensed down to one type
    advance: {
        id: `advance`,
        steps: [
            [`/product`, `/guarantor`],
            {
                path: `/qualify-explain`,
                optional: ({ creditChecked }: OptionalFlowProps) => creditChecked
            },
            {
                path: `/qualification-info`,
                optional: ({ creditChecked }: OptionalFlowProps) => creditChecked
            },
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/confirm-autopay`,
                optional: ({ isAutoPay, idProductType }: OptionalFlowProps) => isAutoPay && idProductType !== Enums.ProductType.SunboundPay
            },
            {
                path: `/add-payors`,
                optional: ({ skipPayors }: OptionalFlowProps) => skipPayors === true
            },
            {
                path: `/guarantor/invoice/payors`,
                optional: ({ skipPayors }: OptionalFlowProps) => skipPayors === true
            },
            {
                path: `/guarantor/invoice/summary`,
                optional: ({ enroll }: OptionalFlowProps) => enroll !== true
            },
            {
                path: `/update-enrollment/summary`,
                optional: ({ update }: OptionalFlowProps) => update !== true
            },
            `/guarantor/invoice/enrollment-completed`,
            `/`
        ]
    },
    //can this be removed? Search Flows.gateway and only id shows up
    gateway: {
        id: `gateway`,
        steps: [
            `/product`,
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod }: OptionalFlowProps) => hasDefaultPaymentMethod
            },
            {
                path: `/confirm-autopay`,
                optional: ({ isAutoPay }: OptionalFlowProps) => isAutoPay
            },
            {
                path: `/add-payors`,
                optional: ({ skipPayors }: OptionalFlowProps) => skipPayors === true
            },
            {
                path: `/guarantor/invoice/payors`,
                optional: ({ skipPayors }: OptionalFlowProps) => skipPayors
            },
            `/guarantor/invoice/summary`,
            `/guarantor/invoice/enrollment-completed`,
            `/`
        ]
    },

    makePaymentAutoPay: {
        id: `makePaymentAutoPay`,
        steps: [
            [`/guarantor`, `/payor`, `/payment-method-settings`],
            {
                path: `/payment-method-selector`,
                optional: ({ hasDefaultPaymentMethod, addDefaultPM }: OptionalFlowProps) => hasDefaultPaymentMethod && addDefaultPM !== true,
            },
            {
                path: `/default-payment-method`,
                optional: ({ hasDefaultPaymentMethod, addDefaultPM }: OptionalFlowProps) => hasDefaultPaymentMethod && addDefaultPM !== true
            },
            {
                path: `/payment-autopay`,
                optional: ({ isAutoPay, makePayment }: OptionalFlowProps) => isAutoPay || makePayment
            },
            {
                path: `/autopay-warning`,
                optional: ({ isAutopayLate }: OptionalFlowProps) => !isAutopayLate
            },
            {
                path: `/autopay-confirmation`,
                optional: ({ isAutoPay, makePayment, cancel }: OptionalFlowProps) => isAutoPay === false || makePayment === true || cancel === true
            },
            {
                path: `/make-payment`,
                optional: ({ makePayment, amount }: OptionalFlowProps) => makePayment !== true || amount
            },
            {
                path: `/confirm-payment`,
                optional: ({ makePayment }: OptionalFlowProps) => makePayment !== true
            },
            {
                path: `/resolve-defaults`,
                optional: ({ resolveDefaults }: OptionalFlowProps) => !resolveDefaults
            },
            `/`
        ]
    },

    addDefaultPaymentMethod: {
        id: `addDefaultPaymentMethod`,
        steps: [
            `/payment-method-settings`,
            `/payment-method-selector`,
            `/default-payment-method`,
            {
                path: `/resolve-defaults`,
                optional: ({ resolveDefaults }: OptionalFlowProps) => !resolveDefaults
            },
            `/payment-method-settings`
        ]
    },
    multiAutopay: {
        id: `multiAutopay`,
        steps: [
            `/payment-method-settings`,
            `/multi-autopay`,
            `/payment-method-settings`
        ]
    },
    sendPasswordReset: {
        id: `sendPasswordReset`,
        steps: [
            [`/login`, `/signup`],
            `/forgot-password`,
            `/forgot-password-info`,
            `/`
        ]
    },
    resendWelcome: {
        id: `resendWelcome`,
        steps: [
            `/signup`,
            `/resend-welcome`,
            `/resend-welcome-info`,
            `/`
        ]
    }
};

export const Help = {
    Phone: `623-263-9162`,
    Email: `help@sunboundhomes.com`,
    FAQ: `https://www.sunboundhomes.com/faq`,
    FAQErrors: `https://help.sunboundhomes.com/hc/en-us/articles/23208159704091-Handling-Errors`,
    AutoPay: `https://help.sunboundhomes.com/hc/en-us/articles/18384985410587`,
    Privacy: `https://www.sunboundhomes.com/privacy-policy`,
    PaymentDay: `https://help.sunboundhomes.com/hc/en-us/articles/18384985410587#01HZMQEKA7QWYY8ZFN8ASRENVH`
};

export const Errors = {
    INTERNAL_ERROR: `Please contact system administrator`,
    PM_REGISTRATION: `There was an issue registering your payment method. Please verify your payment information and try again. {0}`,
    PAYMENT_PLATFORM_ERROR: `Your payment could not be completed due to an unexpected error. Please try again later.`

};

export const System = {
    MIN_PAYMENT_AMOUNT: 5.0
};

export const ImgBucket = `https://images.sunboundhomes.com`;

// Remove if review passes(don't know if other way is actually better)
// export const FailedTransactions = {
//     R01: `Please resolve and try again, or try again with a different payment method.`,
//     R02: `Please try again with a different payment method`,
//     R03: `Please double check your account number, routing number, and account holder name before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R04: `Please double check your account number before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R05: `Please try again with a different payment method`,
//     R06: `Please try again with a different payment method, or contact Sunbound support`,
//     R07: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R08: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R09: `Please resolve and try again, or try again with a different payment method.`,
//     R10: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R11: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R12: `Please contact your financial institution as you may need to update your account and routing numbers, then try again, or try with a different payment method`,
//     R13: `Please double check your routing number before reconnecting this payment method and trying again, or try again with a different payment method.`,
//     R14: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R15: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R16: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R17: `Please contact your bank to see if this was due to a block on transactions. If not, please double check your account information before reconnecting this payment method and trying again. You may also try again with a different payment method`,
//     R20: `Please try again with a different payment method`,
//     R21: `Please try again with a different payment method`,
//     R22: `Please try again with a different payment method`,
//     R23: `Please contact your bank to see why the refund was refused, then contact Sunbound support to discuss options for completing your refund.`,
//     R24: `Please contact Sunbound support.`,
//     R29: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R31: `Please contact your bank about removing the block on transactions, or try again with a different payment method`,
//     R33: `Please try again with a different payment method`
// };

export const FailedTransactionsActions = [
    {
        statusCodes: [`R01`, `R09`],
        action: `Please resolve and try again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R02`, `R05`, `R20`, `R21`, `R22`, `R33`],
        action: `Please try again with a different payment method.`
    },
    {
        statusCodes: [`R03`],
        action: `Please double check your account number, routing number, and account holder name before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R04`],
        action: `Please double check your account number before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R06`],
        action: `Please try again with a different payment method, or contact Sunbound support.`
    },
    {
        statusCodes: [`R07`, `R08`, `R10`, `R11`, `R14`, `R15`, `R16`, `R29`, `R31`],
        action: `Please contact your bank about removing the block on transactions, or try again with a different payment method.`
    },
    {
        statusCodes: [`R12`],
        action: `Please contact your financial institution as you may need to update your account and routing numbers, then try again, or try with a different payment method.`
    },
    {
        statusCodes: [`R13`],
        action: `Please double check your routing number before reconnecting this payment method and trying again, or try again with a different payment method.`
    },
    {
        statusCodes: [`R17`],
        action: `Please contact your bank to see if this was due to a block on transactions. If not, please double check your account information before reconnecting this payment method and trying again. You may also try again with a different payment method.`
    },
    {
        statusCodes: [`R23`],
        action: `Please contact your bank to see why the refund was refused, then contact Sunbound support to discuss options for completing your refund.`
    },
    {
        statusCodes: [`R24`],
        action: `Please contact Sunbound support.`
    }
];
